import {
  type FunctionComponent,
  type ReactNode,
  useRef,
  useState,
} from 'react';

import { ThemeProvider } from '@dr.pogodin/react-utils';

import Separator from 'components/website/Separator';

import { useGlobalState } from 'utils';

import Logo from 'assets/images/logo-soul-sanctuary-new-110px.svg';

import buttonTheme from 'themes/Button/admin/primary-34h.scss';
import customDropdownTheme from 'themes/CustomDropdown/admin/with-inner-label-60px.scss';

import inputTheme from 'themes/Input/admin/with-inner-label-60px.scss';

import Header, { type TabT } from './Header';

import Tab01 from './Tab01';

import Tab02 from './Tab02';

import Tab03 from './Tab03';
import Tab04 from './Tab04';
import type { TabRefT } from './common';

import checkboxTheme from './checkbox.scss';
import S from './style.scss';

const AdminPractitionerApplicationPage: FunctionComponent = () => {
  const [tab, setTab] = useState<TabT>('About You');
  const [tabValid, setTabValid] = useState(true);

  const [user] = useGlobalState('user');
  if (!user) throw Error('Unauthorised');

  const tabRef = useRef<TabRefT>(null);

  let content: ReactNode;
  switch (tab) {
    case 'About You':
      content = (
        <Tab01
          goToNextTab={() => {
            setTab('Practice & Expertise');
          }}
          onValidChanged={setTabValid}
          ref={tabRef}
        />
      );
      break;
    case 'Experience & Training':
      content = (
        <Tab03
          goToNextTab={() => {
            setTab('Portfolio');
          }}
          goToPrevTab={() => {
            setTab('Practice & Expertise');
          }}
          ref={tabRef}
        />
      );
      break;
    case 'Portfolio':
      content = (
        <Tab04
          goToPrev={() => {
            setTab('Experience & Training');
          }}
          ref={tabRef}
        />
      );
      break;
    case 'Practice & Expertise':
      content = (
        <Tab02
          goToNextTab={() => {
            setTab('Experience & Training');
          }}
          goToPrevTab={() => {
            setTab('About You');
          }}
          ref={tabRef}
        />
      );
      break;
    default:
      throw Error('Unexpected tab value');
  }

  return (
    <ThemeProvider
      themes={{
        Button: buttonTheme,
        Checkbox: checkboxTheme,
        CustomDropdown: customDropdownTheme,
        Input: inputTheme,
      }}
    >
      <div className={S.container}>
        <Logo />
        <div styleName="card">
          <h1>We&apos;re excited to meet you! </h1>
          <Separator />
          <p styleName="centered">
            Please complete each section, including a brief introduction video
            that lets us experience your presence and approach.<br />
            You can move between sections freely and save progress as you go.
          </p>
          <Header
            setTab={(nextTab) => {
              if (tabRef.current?.valid) {
                void (async () => {
                  if (!tabRef.current) throw Error('Missing references');
                  await tabRef.current.save();
                  setTab(nextTab);
                })();
              }
            }}
            tab={tab}
            tabValid={tabValid}
          />
          {content}
        </div>
      </div>
    </ThemeProvider>
  );
};

export default AdminPractitionerApplicationPage;
